import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Building2, Tag, FileText, LogOut, Plus, Trash2, TrendingUp, CheckCircle2, MapPin, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const STATUSES = ["New", "Interested", "Follow-up", "Site Visit", "Booked", "Rejected", "Sold"];
const STATUS_COLOR = { New: "bg-blue-100 text-blue-700", Interested: "bg-amber-100 text-amber-700", "Follow-up": "bg-purple-100 text-purple-700", "Site Visit": "bg-cyan-100 text-cyan-700", Booked: "bg-green-100 text-green-700", Rejected: "bg-red-100 text-red-700", Sold: "bg-emerald-100 text-emerald-700" };

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "leads", label: "Leads / CRM", icon: Users },
  { id: "projects", label: "Projects", icon: Building2 },
  { id: "offers", label: "Offers", icon: Tag },
  { id: "blogs", label: "Blogs", icon: FileText },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [offers, setOffers] = useState([]);
  const [blogs, setBlogs] = useState([]);

  const loadAll = () => {
    api.get("/admin/analytics").then((r) => setAnalytics(r.data));
    api.get("/leads").then((r) => setLeads(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/offers").then((r) => setOffers(r.data));
    api.get("/blogs").then((r) => setBlogs(r.data));
  };
  useEffect(() => { loadAll(); }, []);

  const updateLead = async (id, status) => {
    await api.put(`/leads/${id}`, { status });
    setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
    api.get("/admin/analytics").then((r) => setAnalytics(r.data));
    toast.success("Lead updated");
  };
  const delLead = async (id) => { await api.delete(`/leads/${id}`); setLeads((l)=>l.filter(x=>x.id!==id)); toast.success("Lead deleted"); };
  const delProject = async (id) => { await api.delete(`/projects/${id}`); setProjects((p)=>p.filter(x=>x.id!==id)); toast.success("Project deleted"); };
  const delOffer = async (id) => { await api.delete(`/offers/${id}`); setOffers((o)=>o.filter(x=>x.id!==id)); toast.success("Offer deleted"); };
  const delBlog = async (id) => { await api.delete(`/blogs/${id}`); setBlogs((b)=>b.filter(x=>x.id!==id)); toast.success("Blog deleted"); };

  return (
    <div className="font-body min-h-screen bg-[#f4f6f5] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#08341f] text-white flex-col hidden md:flex fixed inset-y-0">
        <div className="px-6 py-6 border-b border-white/10">
          <p className="font-heading font-bold">NEXT GEN <span className="text-[#D4AF37]">CRM</span></p>
          <p className="text-xs text-white/50 mt-1">{user?.email}</p>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`dash-nav-${n.id}`} onClick={()=>setTab(n.id)} className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${tab===n.id?"bg-[#0B5D3B] text-white":"text-white/70 hover:bg-white/5"}`}>
              <n.icon size={18}/> {n.label}
            </button>
          ))}
        </nav>
        <button data-testid="dash-logout" onClick={()=>{logout();nav("/");}} className="m-3 flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-white/70 hover:bg-white/5"><LogOut size={18}/> Logout</button>
      </aside>

      <main className="flex-1 md:ml-64 p-5 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-2xl font-bold text-gray-900 capitalize">{NAV.find(n=>n.id===tab)?.label}</h1>
          <div className="md:hidden"><Select value={tab} onValueChange={setTab}><SelectTrigger className="w-40"><SelectValue/></SelectTrigger><SelectContent>{NAV.map(n=><SelectItem key={n.id} value={n.id}>{n.label}</SelectItem>)}</SelectContent></Select></div>
        </div>

        {tab === "overview" && analytics && (
          <div className="space-y-8" data-testid="dash-overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {[["Total Leads",analytics.total_leads,Users],["Projects",analytics.total_projects,Building2],["Site Visits",analytics.site_visits,MapPin],["Booked",analytics.booked,CheckCircle2]].map(([l,v,I])=>(
                <div key={l} className="bg-white rounded-2xl p-6 shadow-sm">
                  <I className="text-[#0B5D3B]" size={22}/>
                  <p className="font-heading text-3xl font-bold text-gray-900 mt-3">{v}</p>
                  <p className="text-sm text-gray-500">{l}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-heading font-semibold text-gray-900 mb-4">Leads by Source</h3>
                {Object.entries(analytics.sources).map(([s,c])=>(
                  <div key={s} className="flex items-center gap-3 mb-3">
                    <span className="w-24 text-sm text-gray-600">{s}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-[#0B5D3B] rounded-full" style={{width:`${Math.min(100,(c/(analytics.total_leads||1))*100)}%`}}/></div>
                    <span className="text-sm font-semibold text-gray-800 w-6">{c}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-heading font-semibold text-gray-900 mb-4">Leads by Status</h3>
                {Object.entries(analytics.statuses).map(([s,c])=>(
                  <div key={s} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLOR[s]}`}>{s}</span>
                    <span className="text-sm font-semibold text-gray-800">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="dash-leads">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500"><tr>{["Name","Phone","Source","Project","Status","",].map(h=><th key={h} className="px-5 py-3 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {leads.length===0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-400">No leads yet.</td></tr>}
                  {leads.map((l)=>(
                    <tr key={l.id} className="border-t border-gray-50">
                      <td className="px-5 py-3 font-medium text-gray-800">{l.name}</td>
                      <td className="px-5 py-3 text-gray-600">{l.phone}</td>
                      <td className="px-5 py-3 text-gray-600">{l.source}</td>
                      <td className="px-5 py-3 text-gray-600">{l.project_name||"—"}</td>
                      <td className="px-5 py-3">
                        <Select value={l.status} onValueChange={(v)=>updateLead(l.id,v)}>
                          <SelectTrigger data-testid={`lead-status-${l.id}`} className="h-8 w-32 text-xs"><SelectValue/></SelectTrigger>
                          <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3"><button data-testid={`del-lead-${l.id}`} onClick={()=>delLead(l.id)} className="text-red-400 hover:text-red-600"><Trash2 size={16}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "projects" && <ProjectManager projects={projects} onChange={loadAll} onDelete={delProject} />}
        {tab === "offers" && <OfferManager offers={offers} onChange={loadAll} onDelete={delOffer} />}
        {tab === "blogs" && <BlogManager blogs={blogs} onChange={loadAll} onDelete={delBlog} />}
      </main>
    </div>
  );
}

function Uploader({ label, accept, multiple, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        urls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url_path}`);
      }
      onChange(multiple ? [...(Array.isArray(value) ? value : []), ...urls] : urls[urls.length - 1]);
      toast.success(`${label} uploaded`);
    } catch {
      toast.error(`${label} upload failed`);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const count = multiple ? (Array.isArray(value) ? value.length : 0) : (value ? 1 : 0);
  const remove = (i) => onChange(multiple ? value.filter((_, idx) => idx !== i) : "");
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{count > 0 && <span className="text-[#0B5D3B]"> · {count} uploaded</span>}
      </label>
      <input data-testid={testid} type="file" accept={accept} multiple={multiple} onChange={handle} disabled={busy}
        className="w-full text-sm border border-gray-200 rounded-lg file:mr-3 file:rounded-lg file:border-0 file:bg-[#0B5D3B] file:text-white file:px-4 file:py-2 file:text-xs file:font-semibold file:cursor-pointer" />
      {busy && <p className="text-xs text-gray-400 mt-1">Uploading…</p>}
      {multiple && count > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((u, i) => (
            <span key={i} className="flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-[11px] text-gray-600">
              #{i + 1}<button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-600">×</button>
            </span>
          ))}
        </div>
      )}
      {!multiple && value && (
        <div className="flex items-center gap-1.5 mt-2">
          <span className="rounded bg-gray-100 px-2 py-1 text-[11px] text-gray-600">1 file</span>
          <button type="button" onClick={() => remove()} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
        </div>
      )}
    </div>
  );
}

const EMPTY_PROJECT = { name:"", location:"", price:"", size:"", approval:"HMDA", status:"Available", description:"", featured:false, amenities:"", nearby:"", images:[], videos:[], layout_url:"", brochure_url:"", drone_video:"", master_layout:"", map_embed:"" };

function ProjectManager({ projects, onChange }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [f, setF] = useState(EMPTY_PROJECT);

  const openAdd = () => { setEditingId(null); setF(EMPTY_PROJECT); setOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setF({
      name:p.name||"", location:p.location||"", price:p.price||"", size:p.size||"",
      approval:p.approval||"HMDA", status:p.status||"Available", description:p.description||"",
      featured:!!p.featured, amenities:(p.amenities||[]).join(", "), nearby:(p.nearby||[]).join(", "),
      images:p.images||[], videos:p.videos||[], layout_url:p.layout_url||p.master_layout||"",
      brochure_url:p.brochure_url||"", drone_video:p.drone_video||"", master_layout:p.master_layout||"", map_embed:p.map_embed||"",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!f.name || !f.location) return toast.error("Name and location required");
    const payload = {
      ...f,
      master_layout: f.layout_url || f.master_layout,
      amenities: f.amenities.split(",").map(s=>s.trim()).filter(Boolean),
      nearby: f.nearby.split(",").map(s=>s.trim()).filter(Boolean),
    };
    if (editingId) { await api.put(`/projects/${editingId}`, payload); toast.success("Project updated"); }
    else { await api.post("/projects", payload); toast.success("Project added"); }
    setOpen(false); setF(EMPTY_PROJECT); setEditingId(null); onChange();
  };
  const del = async (id) => { await api.delete(`/projects/${id}`); toast.success("Project deleted"); onChange(); };

  const inp = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0B5D3B]";
  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <button data-testid="add-project-btn" onClick={openAdd} className="mb-5 flex items-center gap-2 rounded-full bg-[#0B5D3B] text-white px-5 py-2.5 text-sm font-semibold"><Plus size={16}/> Add Project</button>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Edit Project" : "Add Project"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="pf-name" className={inp} placeholder="Project Name" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})}/>
            <input className={inp} placeholder="Location" value={f.location} onChange={(e)=>setF({...f,location:e.target.value})}/>
            <div className="grid grid-cols-2 gap-3">
              <input className={inp} placeholder="Price (e.g. ₹9,999 / sq.yd)" value={f.price} onChange={(e)=>setF({...f,price:e.target.value})}/>
              <input className={inp} placeholder="Size (e.g. 150-500 sq.yd)" value={f.size} onChange={(e)=>setF({...f,size:e.target.value})}/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={f.approval} onValueChange={(v)=>setF({...f,approval:v})}><SelectTrigger className={inp}><SelectValue/></SelectTrigger><SelectContent><SelectItem value="HMDA">HMDA</SelectItem><SelectItem value="DTCP">DTCP</SelectItem><SelectItem value="FCDA">FCDA</SelectItem></SelectContent></Select>
              <input className={inp} placeholder="Status (e.g. Available)" value={f.status} onChange={(e)=>setF({...f,status:e.target.value})}/>
            </div>
            <textarea className={inp} rows={3} placeholder="Description" value={f.description} onChange={(e)=>setF({...f,description:e.target.value})}/>
            <input className={inp} placeholder="Amenities (comma separated)" value={f.amenities} onChange={(e)=>setF({...f,amenities:e.target.value})}/>
            <input className={inp} placeholder="Nearby places (comma separated)" value={f.nearby} onChange={(e)=>setF({...f,nearby:e.target.value})}/>
            <div className="border-t border-gray-100 pt-3 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Uploads</p>
              <Uploader testid="upload-project-photos" label="Project Photos" accept="image/*" multiple value={f.images} onChange={(v)=>setF({...f,images:v})}/>
              <Uploader testid="upload-project-videos" label="Project Videos" accept="video/*" multiple value={f.videos} onChange={(v)=>setF({...f,videos:v})}/>
              <Uploader testid="upload-drone-video" label="Drone Video" accept="video/*" value={f.drone_video} onChange={(v)=>setF({...f,drone_video:v})}/>
              <Uploader testid="upload-layout" label="Layout Download" accept="image/*,application/pdf" value={f.layout_url} onChange={(v)=>setF({...f,layout_url:v})}/>
              <Uploader testid="upload-brochure" label="Brochure Download" accept="application/pdf,image/*" value={f.brochure_url} onChange={(v)=>setF({...f,brochure_url:v})}/>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e)=>setF({...f,featured:e.target.checked})}/> Featured on homepage</label>
            <button data-testid="pf-save" onClick={save} className="w-full rounded-full bg-[#0B5D3B] text-white py-3 font-semibold">{editingId ? "Update Project" : "Save Project"}</button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {projects.map((p)=>(
          <div key={p.id} className="bg-white rounded-xl overflow-hidden shadow-sm">
            {p.images?.[0] ? <img src={p.images[0]} alt="" className="h-36 w-full object-cover"/> : <div className="h-36 w-full bg-[#0B5D3B]/10 grid place-items-center text-[#0B5D3B]"><Building2 size={28}/></div>}
            <div className="p-4">
              <div className="flex justify-between items-start gap-2">
                <h4 className="font-heading font-semibold text-gray-900">{p.name}</h4>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`edit-project-${p.id}`} onClick={()=>openEdit(p)} className="text-gray-400 hover:text-[#0B5D3B]"><Pencil size={15}/></button>
                  <button data-testid={`del-project-${p.id}`} onClick={()=>del(p.id)} className="text-red-400 hover:text-red-600"><Trash2 size={15}/></button>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">{p.location}</p>
              <p className="text-sm font-semibold text-[#0B5D3B] mt-2">{p.price} · {p.approval}{p.featured && " · ⭐ Featured"}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OfferManager({ offers, onChange, onDelete }) {
  const [f, setF] = useState({ title:"", description:"", badge:"Limited Time" });
  const save = async () => { if(!f.title) return toast.error("Title required"); await api.post("/offers", {...f, active:true}); toast.success("Offer added"); setF({title:"",description:"",badge:"Limited Time"}); onChange(); };
  const inp = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0B5D3B]";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm space-y-3 h-fit">
        <h3 className="font-heading font-semibold">Add Offer</h3>
        <input data-testid="of-title" className={inp} placeholder="Offer Title" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})}/>
        <textarea className={inp} rows={3} placeholder="Description" value={f.description} onChange={(e)=>setF({...f,description:e.target.value})}/>
        <input className={inp} placeholder="Badge" value={f.badge} onChange={(e)=>setF({...f,badge:e.target.value})}/>
        <button data-testid="of-save" onClick={save} className="w-full rounded-full bg-[#0B5D3B] text-white py-2.5 font-semibold">Add Offer</button>
      </div>
      <div className="lg:col-span-2 space-y-3">
        {offers.map((o)=>(
          <div key={o.id} className="bg-white rounded-xl p-5 shadow-sm flex justify-between items-start">
            <div><span className="text-xs rounded-full bg-[#D4AF37]/20 text-[#8a6d13] px-2.5 py-1 font-semibold">{o.badge}</span><h4 className="font-heading font-semibold text-gray-900 mt-2">{o.title}</h4><p className="text-sm text-gray-500 mt-1">{o.description}</p></div>
            <button data-testid={`del-offer-${o.id}`} onClick={()=>onDelete(o.id)} className="text-red-400 hover:text-red-600"><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlogManager({ blogs, onChange, onDelete }) {
  const [f, setF] = useState({ title:"", category:"Guide", excerpt:"", content:"", image:"" });
  const save = async () => { if(!f.title) return toast.error("Title required"); await api.post("/blogs", f); toast.success("Blog added"); setF({title:"",category:"Guide",excerpt:"",content:"",image:""}); onChange(); };
  const inp = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0B5D3B]";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm space-y-3 h-fit">
        <h3 className="font-heading font-semibold">Add Blog</h3>
        <input data-testid="bf-title" className={inp} placeholder="Title" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})}/>
        <input className={inp} placeholder="Category" value={f.category} onChange={(e)=>setF({...f,category:e.target.value})}/>
        <input className={inp} placeholder="Image URL" value={f.image} onChange={(e)=>setF({...f,image:e.target.value})}/>
        <textarea className={inp} rows={2} placeholder="Excerpt" value={f.excerpt} onChange={(e)=>setF({...f,excerpt:e.target.value})}/>
        <textarea className={inp} rows={4} placeholder="Content" value={f.content} onChange={(e)=>setF({...f,content:e.target.value})}/>
        <button data-testid="bf-save" onClick={save} className="w-full rounded-full bg-[#0B5D3B] text-white py-2.5 font-semibold">Publish Blog</button>
      </div>
      <div className="lg:col-span-2 space-y-3">
        {blogs.map((b)=>(
          <div key={b.id} className="bg-white rounded-xl p-4 shadow-sm flex gap-4 items-center">
            {b.image ? <img src={b.image} alt="" className="w-20 h-16 rounded-lg object-cover"/> : <div className="w-20 h-16 rounded-lg bg-[#0B5D3B]/10 grid place-items-center text-[#0B5D3B]"><FileText size={20}/></div>}
            <div className="flex-1"><span className="text-xs text-[#D4AF37] font-semibold">{b.category}</span><h4 className="font-heading font-semibold text-gray-900">{b.title}</h4></div>
            <button data-testid={`del-blog-${b.id}`} onClick={()=>onDelete(b.id)} className="text-red-400 hover:text-red-600"><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}
